import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Eye, EyeOff, Copy, Pencil, Trash2, Wifi, KeyRound, Phone, Shield, Lock, Home } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { value: "Wi-Fi", icon: Wifi },
  { value: "Portkod", icon: KeyRound },
  { value: "Larmkod", icon: Shield },
  { value: "Telefon", icon: Phone },
  { value: "Försäkring", icon: Lock },
  { value: "Hem", icon: Home },
  { value: "Övrigt", icon: KeyRound },
];

const iconFor = (cat) => CATEGORIES.find((c) => c.value === cat)?.icon || KeyRound;

const emptyForm = { category: "Wi-Fi", label: "", value: "", notes: "" };

export default function VaultPage() {
  const [codes, setCodes] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [revealed, setRevealed] = useState({});

  const load = async () => {
    const { data } = await api.get("/codes");
    setCodes(data);
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({ category: c.category, label: c.label, value: c.value, notes: c.notes });
    setOpen(true);
  };

  const save = async () => {
    if (!form.label.trim() || !form.value.trim()) {
      toast.error("Etikett och värde krävs");
      return;
    }
    try {
      if (editing) await api.put(`/codes/${editing.id}`, form);
      else await api.post("/codes", form);
      toast.success(editing ? "Uppdaterad" : "Tillagd");
      setOpen(false);
      load();
    } catch (e) {
      toast.error("Kunde inte spara");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Ta bort?")) return;
    await api.delete(`/codes/${id}`);
    toast.success("Borttagen");
    load();
  };

  const copy = (val) => {
    navigator.clipboard.writeText(val);
    toast.success("Kopierad");
  };

  const grouped = codes.reduce((acc, c) => {
    acc[c.category] = acc[c.category] || [];
    acc[c.category].push(c);
    return acc;
  }, {});

  return (
    <div className="px-5 pt-3" data-testid="vault-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="label-eyebrow">Familjevalv</p>
          <h1 className="font-display text-2xl font-medium mt-0.5">Viktiga koder</h1>
        </div>
        <Button
          onClick={openCreate}
          size="sm"
          className="rounded-full bg-[color:var(--moss)] hover:bg-[#1f421f] h-9"
          data-testid="add-code-button"
        >
          <Plus size={16} className="mr-1" /> Ny
        </Button>
      </div>

      {codes.length === 0 ? (
        <div className="card-soft p-8 text-center" data-testid="vault-empty">
          <KeyRound size={28} className="mx-auto text-[color:var(--text-tertiary)] mb-3" />
          <p className="text-sm text-[color:var(--text-secondary)]">
            Spara portkoder, Wi-Fi-lösenord och annat viktigt här.
          </p>
        </div>
      ) : (
        Object.entries(grouped).map(([cat, list]) => {
          const Icon = iconFor(cat);
          return (
            <div key={cat} className="mb-5">
              <h3 className="font-display text-base font-medium mb-2 flex items-center gap-2">
                <Icon size={15} className="text-[color:var(--moss)]" />
                {cat}
              </h3>
              <ul className="space-y-2 stagger">
                {list.map((c) => {
                  const isRevealed = revealed[c.id];
                  return (
                    <li
                      key={c.id}
                      className="card-soft p-3.5 fade-up"
                      data-testid={`code-${c.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium">{c.label}</p>
                          <p
                            className={`font-mono text-base mt-1 ${
                              isRevealed ? "text-[color:var(--text-primary)]" : "tracking-[0.3em] text-[color:var(--text-secondary)]"
                            }`}
                            data-testid={`code-value-${c.id}`}
                          >
                            {isRevealed ? c.value : "••••••••"}
                          </p>
                          {c.notes && (
                            <p className="text-xs text-[color:var(--text-tertiary)] mt-1.5">{c.notes}</p>
                          )}
                        </div>
                        <div className="flex flex-col gap-1">
                          <button
                            onClick={() => setRevealed({ ...revealed, [c.id]: !isRevealed })}
                            className="p-1.5 rounded hover:bg-[color:var(--bg-elevated)] text-[color:var(--text-secondary)]"
                            data-testid={`code-reveal-${c.id}`}
                          >
                            {isRevealed ? <EyeOff size={15} /> : <Eye size={15} />}
                          </button>
                          <button
                            onClick={() => copy(c.value)}
                            className="p-1.5 rounded hover:bg-[color:var(--bg-elevated)] text-[color:var(--text-secondary)]"
                            data-testid={`code-copy-${c.id}`}
                          >
                            <Copy size={15} />
                          </button>
                          <button
                            onClick={() => openEdit(c)}
                            className="p-1.5 rounded hover:bg-[color:var(--bg-elevated)] text-[color:var(--text-secondary)]"
                            data-testid={`code-edit-${c.id}`}
                          >
                            <Pencil size={15} />
                          </button>
                          <button
                            onClick={() => remove(c.id)}
                            className="p-1.5 rounded hover:bg-[color:var(--bg-elevated)] text-[color:var(--text-secondary)] hover:text-[#DC2626]"
                            data-testid={`code-delete-${c.id}`}
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md rounded-2xl" data-testid="code-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Redigera" : "Ny kod"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="label-eyebrow block mb-1.5">Kategori</label>
              <div className="grid grid-cols-4 gap-1.5">
                {CATEGORIES.map((c) => {
                  const I = c.icon;
                  return (
                    <button
                      key={c.value}
                      onClick={() => setForm({ ...form, category: c.value })}
                      className={`p-2.5 rounded-xl border text-[10px] flex flex-col items-center gap-1 ${
                        form.category === c.value
                          ? "bg-[color:var(--moss)] text-white border-[color:var(--moss)]"
                          : "bg-white text-[color:var(--text-secondary)] border-[color:var(--border-subtle)]"
                      }`}
                      data-testid={`category-${c.value}`}
                    >
                      <I size={14} />
                      <span>{c.value}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="label-eyebrow block mb-1.5">Etikett</label>
              <Input
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="t.ex. Hemnätverk"
                className="rounded-xl"
                data-testid="code-label-input"
              />
            </div>
            <div>
              <label className="label-eyebrow block mb-1.5">Värde</label>
              <Input
                value={form.value}
                onChange={(e) => setForm({ ...form, value: e.target.value })}
                placeholder="t.ex. SuperSecret123"
                className="rounded-xl font-mono"
                data-testid="code-value-input"
              />
            </div>
            <div>
              <label className="label-eyebrow block mb-1.5">Anteckning</label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Valfri info..."
                className="rounded-xl resize-none"
                rows={2}
                data-testid="code-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} data-testid="code-cancel-button">
              Avbryt
            </Button>
            <Button
              onClick={save}
              className="bg-[color:var(--moss)] hover:bg-[#1f421f]"
              data-testid="code-save-button"
            >
              Spara
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
